import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus, Search, Calendar, Clock, User, Phone, 
  CheckCircle, AlertTriangle, TrendingUp, Euro,
  ChevronRight, Target, Award, Activity, Settings,
  Truck, MapPin, Users, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types d'attelage
const TYPES_ATTELAGE = [
  { id: 'simple', nom: 'Attelage Simple', description: 'Un seul cheval', icon: '🐴' },
  { id: 'paire', nom: 'Attelage en Paire', description: 'Deux chevaux côte à côte', icon: '🐴🐴' },
  { id: 'tandem', nom: 'Attelage Tandem', description: 'Deux chevaux en file', icon: '🐴↔🐴' },
  { id: 'team', nom: 'Team (4+)', description: 'Quatre chevaux ou plus', icon: '🐴🐴🐴🐴' }
];

// Types de voitures
const TYPES_VOITURES = [
  { id: 'marathon', nom: 'Voiture de Marathon', usage: 'Compétition' },
  { id: 'presentation', nom: 'Voiture de Présentation', usage: 'Concours' },
  { id: 'travail', nom: 'Voiture de Travail', usage: 'Entraînement' },
  { id: 'promenade', nom: 'Calèche/Promenade', usage: 'Tourisme' },
  { id: 'agricole', nom: 'Chariot Agricole', usage: 'Travail des champs' }
];

// Niveaux de compétition
const NIVEAUX_COMPETITION = [
  { id: 'club', nom: 'Club', description: 'Niveau débutant' },
  { id: 'amateur', nom: 'Amateur', description: 'Niveau intermédiaire' },
  { id: 'pro', nom: 'Pro', description: 'Niveau professionnel' },
  { id: 'international', nom: 'International', description: 'Niveau élite' }
];

export default function AttelageEquinPage() {
  const { activeSociete } = useWorkspace();
  const [activeTab, setActiveTab] = useState('equipages');
  const [loading, setLoading] = useState(true);
  const [equipages, setEquipages] = useState([]);
  const [voitures, setVoitures] = useState([]);
  const [evenements, setEvenements] = useState([]);
  const [stats, setStats] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddVoitureModal, setShowAddVoitureModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [newEquipage, setNewEquipage] = useState({
    nom: '',
    type_attelage: 'simple',
    chevaux: [],
    meneur_nom: '',
    meneur_telephone: '',
    niveau: 'club',
    voiture_id: '',
    notes: ''
  });

  const [newVoiture, setNewVoiture] = useState({
    nom: '',
    type: 'travail',
    marque: '',
    annee: '',
    etat: 'bon',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'X-Societe-Id': activeSociete?.id || ''
    };

    try {
      // Fetch equipages
      const resEquipages = await fetch(`${API_URL}/api/equin/attelage/equipages`, { headers });
      if (resEquipages.ok) {
        const data = await resEquipages.json();
        setEquipages(data.equipages || []);
        setStats(data.stats || {});
      }

      // Fetch voitures
      const resVoitures = await fetch(`${API_URL}/api/equin/attelage/voitures`, { headers });
      if (resVoitures.ok) {
        const data = await resVoitures.json();
        setVoitures(data.voitures || []);
      }

      // Fetch événements
      const resEvents = await fetch(`${API_URL}/api/equin/attelage/evenements`, { headers });
      if (resEvents.ok) {
        const data = await resEvents.json();
        setEvenements(data.evenements || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSociete]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateEquipage = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/equin/attelage/equipages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify(newEquipage)
      });

      if (res.ok) {
        toast.success('Équipage créé avec succès');
        setShowAddModal(false);
        fetchData();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleCreateVoiture = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/equin/attelage/voitures`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify(newVoiture)
      });

      if (res.ok) {
        toast.success('Voiture ajoutée');
        setShowAddVoitureModal(false);
        fetchData();
      } else {
        toast.error('Erreur lors de l\'ajout');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const filteredEquipages = equipages.filter(e =>
    e.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.meneur_nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50 to-orange-50 p-6" data-testid="attelage-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-stone-900">Attelage</h1>
            <p className="text-stone-600">Gestion des équipages, voitures et compétitions d'attelage</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddVoitureModal(true)} variant="outline">
              <Truck className="w-4 h-4 mr-2" /> Ajouter voiture
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="bg-stone-700 hover:bg-stone-800">
              <Plus className="w-4 h-4 mr-2" /> Nouvel équipage
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Équipages</p>
                <p className="text-2xl font-bold text-stone-700">{equipages.length}</p>
              </div>
              <Users className="w-8 h-8 text-stone-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Voitures</p>
                <p className="text-2xl font-bold text-amber-600">{voitures.length}</p>
              </div>
              <Truck className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Événements à venir</p>
                <p className="text-2xl font-bold text-blue-600">{evenements.filter(e => new Date(e.date) > new Date()).length}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Podiums cette année</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.podiums || 0}</p>
              </div>
              <Award className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white/80">
          <TabsTrigger value="equipages">Équipages</TabsTrigger>
          <TabsTrigger value="voitures">Voitures</TabsTrigger>
          <TabsTrigger value="calendrier">Calendrier</TabsTrigger>
          <TabsTrigger value="palmares">Palmarès</TabsTrigger>
        </TabsList>

        <TabsContent value="equipages">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Mes équipages</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Chargement...</div>
              ) : filteredEquipages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Truck className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Aucun équipage configuré</p>
                  <Button onClick={() => setShowAddModal(true)} variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" /> Créer un équipage
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredEquipages.map((equipage) => {
                    const typeAttelage = TYPES_ATTELAGE.find(t => t.id === equipage.type_attelage);
                    return (
                      <div
                        key={equipage.id}
                        className="border rounded-lg p-4 hover:bg-stone-50 transition"
                        data-testid={`equipage-${equipage.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="text-2xl">{typeAttelage?.icon || '🐴'}</div>
                            <div>
                              <h3 className="font-semibold">{equipage.nom}</h3>
                              <p className="text-sm text-gray-500">{typeAttelage?.nom}</p>
                            </div>
                          </div>
                          <Badge variant="outline">{equipage.niveau}</Badge>
                        </div>
                        <div className="space-y-1 text-sm">
                          <p><User className="w-3 h-3 inline mr-1" /> Meneur: {equipage.meneur_nom}</p>
                          <p><span className="text-gray-500">Chevaux:</span> {equipage.chevaux?.join(', ') || '-'}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="voitures">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle>Parc de voitures</CardTitle>
              <CardDescription>Voitures d'attelage disponibles</CardDescription>
            </CardHeader>
            <CardContent>
              {voitures.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Truck className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Aucune voiture enregistrée</p>
                  <Button onClick={() => setShowAddVoitureModal(true)} variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter une voiture
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {voitures.map((voiture) => {
                    const typeVoiture = TYPES_VOITURES.find(t => t.id === voiture.type);
                    return (
                      <div key={voiture.id} className="border rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <Truck className="w-6 h-6 text-stone-600" />
                          <div>
                            <h3 className="font-semibold">{voiture.nom}</h3>
                            <p className="text-sm text-gray-500">{typeVoiture?.nom}</p>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {voiture.marque && <p>Marque: {voiture.marque}</p>}
                          {voiture.annee && <p>Année: {voiture.annee}</p>}
                          <Badge className={
                            voiture.etat === 'bon' ? 'bg-green-100 text-green-800' :
                            voiture.etat === 'moyen' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }>{voiture.etat}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendrier">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle>Calendrier des événements</CardTitle>
              <CardDescription>Concours, sorties et entraînements</CardDescription>
            </CardHeader>
            <CardContent>
              {evenements.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Aucun événement planifié</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {evenements.sort((a, b) => new Date(a.date) - new Date(b.date)).map((event) => (
                    <div key={event.id} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-stone-700">
                            {new Date(event.date).getDate()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(event.date).toLocaleDateString('fr-FR', { month: 'short' })}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium">{event.nom}</h4>
                          <p className="text-sm text-gray-500">
                            <MapPin className="w-3 h-3 inline mr-1" />{event.lieu}
                          </p>
                        </div>
                      </div>
                      <Badge>{event.type}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="palmares">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle>Palmarès</CardTitle>
              <CardDescription>Résultats en compétition</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <Award className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
                <p>Le palmarès sera affiché ici</p>
                <p className="text-sm">Ajoutez vos résultats de compétition</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Ajout Équipage */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvel équipage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom de l'équipage *</Label>
              <Input
                value={newEquipage.nom}
                onChange={(e) => setNewEquipage({ ...newEquipage, nom: e.target.value })}
                placeholder="Ex: Équipage Champion"
              />
            </div>
            <div>
              <Label>Type d'attelage</Label>
              <Select value={newEquipage.type_attelage} onValueChange={(v) => setNewEquipage({ ...newEquipage, type_attelage: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES_ATTELAGE.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.icon} {t.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Meneur *</Label>
                <Input
                  value={newEquipage.meneur_nom}
                  onChange={(e) => setNewEquipage({ ...newEquipage, meneur_nom: e.target.value })}
                  placeholder="Nom du meneur"
                />
              </div>
              <div>
                <Label>Niveau</Label>
                <Select value={newEquipage.niveau} onValueChange={(v) => setNewEquipage({ ...newEquipage, niveau: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NIVEAUX_COMPETITION.map(n => (
                      <SelectItem key={n.id} value={n.id}>{n.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newEquipage.notes}
                onChange={(e) => setNewEquipage({ ...newEquipage, notes: e.target.value })}
                placeholder="Observations..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Annuler</Button>
            <Button onClick={handleCreateEquipage} className="bg-stone-700 hover:bg-stone-800">
              Créer l'équipage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Ajout Voiture */}
      <Dialog open={showAddVoitureModal} onOpenChange={setShowAddVoitureModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter une voiture</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={newVoiture.nom}
                onChange={(e) => setNewVoiture({ ...newVoiture, nom: e.target.value })}
                placeholder="Ex: Marathon Pro"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newVoiture.type} onValueChange={(v) => setNewVoiture({ ...newVoiture, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES_VOITURES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.nom} ({t.usage})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Marque</Label>
                <Input
                  value={newVoiture.marque}
                  onChange={(e) => setNewVoiture({ ...newVoiture, marque: e.target.value })}
                />
              </div>
              <div>
                <Label>Année</Label>
                <Input
                  value={newVoiture.annee}
                  onChange={(e) => setNewVoiture({ ...newVoiture, annee: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>État</Label>
              <Select value={newVoiture.etat} onValueChange={(v) => setNewVoiture({ ...newVoiture, etat: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="bon">Bon</SelectItem>
                  <SelectItem value="moyen">Moyen</SelectItem>
                  <SelectItem value="mauvais">À réparer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddVoitureModal(false)}>Annuler</Button>
            <Button onClick={handleCreateVoiture} className="bg-stone-700 hover:bg-stone-800">
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
